import { useCallback, useMemo } from 'react';
import { ItemCategory } from '@prisma/client';
import useCatalog from '../catalog/useCatalog';
import useAttributes from '../catalog/useAttributes';
import useCheckoutFormState from './useCheckoutFormState';
import {
  EMPTY_SELECTION,
  PickerSelection,
  brandTiles,
  buildFilterChips,
  categoryTiles,
  flattenCatalog,
  productsInView,
  rowMatches,
  sortVariants,
  typeTiles,
} from '../../utils/catalog/catalogFilters';
import {
  buildSearchIndex,
  searchCatalog,
} from '../../utils/catalog/searchCatalog';

function useCatalogPicker(enabled: boolean) {
  const catalogQuery = useCatalog(enabled);
  const attributesQuery = useAttributes(enabled);
  const { state, dispatch } = useCheckoutFormState();
  const { picker } = state;
  const { query } = picker;
  const selection = useMemo<PickerSelection>(
    () => ({
      category: picker.category,
      brandId: picker.brandId,
      typeLabel: picker.typeLabel,
      attributeValues: picker.attributeValues,
    }),
    [picker.category, picker.brandId, picker.typeLabel, picker.attributeValues],
  );

  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const attributes = useMemo(
    () => attributesQuery.data ?? [],
    [attributesQuery.data],
  );
  const rows = useMemo(() => flattenCatalog(catalog), [catalog]);
  const searchIndex = useMemo(() => buildSearchIndex(catalog), [catalog]);

  const derived = useMemo(() => {
    const products = productsInView(rows, selection).map(entry => ({
      ...entry,
      variants: sortVariants(entry.variants, attributes),
    }));
    return {
      categories: categoryTiles(catalog),
      brands: selection.category ? brandTiles(rows, selection) : [],
      types: selection.category ? typeTiles(rows, selection) : [],
      chips: selection.category
        ? buildFilterChips(rows, attributes, selection)
        : [],
      products,
    };
  }, [rows, catalog, attributes, selection]);

  // Search runs over whatever the directory and chips currently show
  const searchResults = useMemo(
    () =>
      searchCatalog(
        searchIndex.filter(entry => rowMatches(entry.row, selection)),
        query,
      ).map(entry => ({
        ...entry,
        variants: sortVariants(entry.variants, attributes),
      })),
    [searchIndex, query, selection, attributes],
  );

  const update = useCallback(
    (patch: Partial<PickerSelection & { query: string }>) =>
      dispatch({ type: 'SET_PICKER', payload: patch }),
    [dispatch],
  );

  // Chip selections belong to a category; switching categories starts clean
  const selectCategory = useCallback(
    (category: ItemCategory | null) => update({ ...EMPTY_SELECTION, category }),
    [update],
  );

  const toggleBrand = useCallback(
    (brandId: string) =>
      update({ brandId: selection.brandId === brandId ? null : brandId }),
    [update, selection.brandId],
  );

  const toggleType = useCallback(
    (typeLabel: string) =>
      update({
        typeLabel: selection.typeLabel === typeLabel ? null : typeLabel,
      }),
    [update, selection.typeLabel],
  );

  const toggleAttributeValue = useCallback(
    (key: string, label: string) => {
      const current = selection.attributeValues[key] ?? [];
      const next = current.includes(label)
        ? current.filter(l => l !== label)
        : [...current, label];
      update({
        attributeValues: { ...selection.attributeValues, [key]: next },
      });
    },
    [update, selection.attributeValues],
  );

  const clearAttribute = useCallback(
    (key: string) => {
      const rest = Object.fromEntries(
        Object.entries(selection.attributeValues).filter(([k]) => k !== key),
      );
      update({ attributeValues: rest });
    },
    [update, selection.attributeValues],
  );

  const setQuery = useCallback(
    (value: string) => update({ query: value }),
    [update],
  );

  return {
    isLoading: catalogQuery.isLoading || attributesQuery.isLoading,
    isError: catalogQuery.isError || attributesQuery.isError,
    selection,
    query,
    attributes,
    ...derived,
    searchResults,
    selectCategory,
    toggleBrand,
    toggleType,
    toggleAttributeValue,
    clearAttribute,
    setQuery,
  };
}

export type CatalogPickerModel = ReturnType<typeof useCatalogPicker>;

export default useCatalogPicker;
